// Оплата Premium у нативному Android-додатку через Google Play Billing (RevenueCat).
// Веб/Telegram купують через WayForPay — сюди не потрапляють. Політика Google
// вимагає, щоб цифрові покупки в апці йшли через Play Billing, тож тут ми:
//   1. Ініціалізуємо RevenueCat публічним ключем + логінимось нашим User.id
//      (щоб вебхук на сервері знав кому нарахувати Premium).
//   2. Тягнемо ціни зі стору (Google сам показує локальну ціну).
//   3. purchaseStoreProduct → системний екран оплати Google.
//   4. Після оплати пуляємо GET /premium, поки сервер (через вебхук) не активує.
//
// SDK підвантажується динамічно — тільки коли компонент рендериться в апці,
// тож на веб-збірку це не впливає.
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { IconTile } from './AppIcons';

// Наші plan.id → product ID в Google Play Console (мають збігатися з сервером).
const PRODUCT_BY_PLAN: Record<string, string> = {
  '1m': 'premium_1m',
  '3m': 'premium_3m',
  '6m': 'premium_6m',
};

type StoreProduct = { identifier: string; title: string; description: string; priceString: string };
type PlanInput = { id: string; months: number };

export function NativePurchase({
  appUserId,
  publicKey,
  plans,
  onActivated,
}: {
  appUserId?: string;
  publicKey?: string | null;
  plans: PlanInput[];
  onActivated: () => void;
}) {
  const [ready, setReady] = useState(false);
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // productId, 'restore' або null
  // Плагін Play Billing недоступний (стара версія апки без RevenueCat) → редірект.
  const [unavailable, setUnavailable] = useState(false);
  const configured = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!publicKey || !appUserId) {
        setError('Оплата в додатку тимчасово недоступна. Онови додаток або напиши в підтримку.');
        return;
      }
      try {
        const { Purchases, LOG_LEVEL, PRODUCT_CATEGORY } = await import('@revenuecat/purchases-capacitor');
        if (!configured.current) {
          await Purchases.setLogLevel({ level: LOG_LEVEL.ERROR });
          await Purchases.configure({ apiKey: publicKey, appUserID: appUserId });
          configured.current = true;
        }
        const ids = plans.map((p) => PRODUCT_BY_PLAN[p.id]).filter((x): x is string => !!x);
        const res = await Purchases.getProducts({
          productIdentifiers: ids,
          type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
        });
        if (cancelled) return;
        const list = (res.products ?? []) as unknown as StoreProduct[];
        setProducts(list);
        setReady(true);
        if (!list.length) {
          setError('Тарифи ще підвантажуються з Google Play. Спробуй за хвилину.');
        }
      } catch {
        // Плагін не вбудований у цю версію апки (стара збірка) або SDK впав —
        // не показуємо помилку, а падаємо у fallback (покупка на сайті).
        if (!cancelled) setUnavailable(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicKey, appUserId]);

  // Після покупки сервер активує Premium через вебхук RevenueCat (зазвичай ~секунда).
  // Пуляємо статус до ~24с.
  async function pollPremium(): Promise<boolean> {
    for (let i = 0; i < 12; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const d = await api.premium.get();
        if (d.isPremium) return true;
      } catch {
        /* ігноруємо тимчасові помилки мережі */
      }
    }
    return false;
  }

  // Новий формат разових товарів Google може повертати identifier як
  // 'premium_1m' АБО 'premium_1m:base' (product:purchaseOption) — матчимо обидва.
  function findProduct(productId: string): StoreProduct | undefined {
    return products.find(
      (p) => p.identifier === productId || p.identifier.startsWith(productId + ':'),
    );
  }

  async function buy(planId: string) {
    if (busy) return;
    const productId = PRODUCT_BY_PLAN[planId];
    if (!productId) return;
    const product = findProduct(productId);
    if (!product) return;
    setError(null);
    setStatus(null);
    setBusy(productId);
    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      haptic.medium();
      await Purchases.purchaseStoreProduct({ product: product as never });
      setStatus('Оплата отримана. Активуємо Premium…');
      const ok = await pollPremium();
      if (ok) {
        haptic.success();
        onActivated();
      } else {
        setStatus('Оплата отримана. Premium активується за хвилину — онови сторінку трохи згодом.');
      }
    } catch (e: unknown) {
      const err = e as { code?: unknown; userCancelled?: unknown; message?: unknown };
      const isCancel =
        String(err?.code) === '1' ||
        err?.userCancelled === true ||
        /cancel/i.test(String(err?.message ?? ''));
      if (!isCancel) {
        setError('Оплата не пройшла. Спробуй ще раз або напиши в підтримку.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function restore() {
    if (busy) return;
    setError(null);
    setStatus(null);
    setBusy('restore');
    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      await Purchases.restorePurchases();
      setStatus('Перевіряємо покупки…');
      const ok = await pollPremium();
      if (ok) {
        haptic.success();
        onActivated();
      } else {
        setStatus('Активних покупок не знайдено.');
      }
    } catch {
      setError('Не вдалося відновити покупки.');
    } finally {
      setBusy(null);
    }
  }

  const monthsLabel = (m: number) => (m === 1 ? '1 місяць' : m < 5 ? `${m} місяці` : `${m} місяців`);

  // Play Billing недоступний (нема ключа АБО стара апка без плагіна).
  // ВАЖЛИВО: у нативній апці ЗАБОРОНЕНО вести юзера на зовнішню оплату (правила
  // Google Play, anti-steering) — жодних посилань на WayForPay/сайт. Показуємо
  // нейтральне повідомлення + вхід в акаунт (це дозволено: Premium, куплений деінде,
  // може відкриватись за входом у той самий акаунт).
  if (!publicKey || !appUserId || unavailable) {
    return (
      <section className="card space-y-3 text-center">
        <div className="flex justify-center">
          <IconTile kind="crown" className="h-12 w-12" glyphClass="h-7 w-7" />
        </div>
        <p className="text-sm text-tg-text">
          Оформлення Premium тимчасово недоступне. Спробуй трохи пізніше.
        </p>
        <p className="text-xs text-tg-hint">
          Якщо Premium у тебе вже є — увійди тим самим акаунтом, і він активується
          автоматично на цьому профілі.
        </p>
      </section>
    );
  }

  return (
    <section className="card space-y-3">
      <div className="flex items-center gap-3">
        <IconTile kind="crown" className="h-11 w-11" />
        <div>
          <div className="font-semibold text-tg-text">Оформити Premium</div>
          <div className="text-xs text-tg-hint">Оплата через Google Play</div>
        </div>
      </div>

      {!ready && !error && (
        <div className="py-4 text-center text-sm text-tg-hint">Завантажуємо тарифи…</div>
      )}

      {ready &&
        plans.map((p) => {
          const productId = PRODUCT_BY_PLAN[p.id];
          const product = productId ? findProduct(productId) : undefined;
          if (!product) return null;
          const isBusy = busy === productId;
          return (
            <button
              key={p.id}
              onClick={() => buy(p.id)}
              disabled={!!busy}
              className="flex w-full items-center justify-between rounded-xl border-2 border-transparent bg-tg-bg px-4 py-3 text-left transition active:scale-[0.99] disabled:opacity-50"
            >
              <div>
                <div className="font-semibold text-tg-text">{monthsLabel(p.months)}</div>
                <div className="text-xs text-tg-hint">Premium-доступ</div>
              </div>
              <div className="rounded-lg bg-tg-success px-3 py-1.5 text-sm font-bold text-white">
                {isBusy ? '…' : product.priceString}
              </div>
            </button>
          );
        })}

      {status && <div className="text-center text-sm text-tg-success">{status}</div>}
      {error && <div className="text-center text-sm text-tg-destructive">{error}</div>}

      <button
        onClick={restore}
        disabled={!!busy || !ready}
        className="w-full text-center text-xs text-tg-hint underline-offset-4 hover:underline disabled:opacity-50"
      >
        Відновити покупки
      </button>

      <p className="text-center text-[11px] text-tg-hint">
        Premium активується автоматично після оплати. Той самий Premium діє й на сайті, і в
        Telegram під цим акаунтом.
      </p>
    </section>
  );
}
